import { ref, type Raw, type Ref } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import { useWait } from '@/app/composables/useWait'
import { AuthMode } from './types'
import { SignupView } from './models'
import type { SocialAuthComponentView } from './social'

export interface SignupAuthOptions {
  onSuccess: (nextPage: RouteLocationRaw | null) => void | Promise<void>
}

interface SignupAuthState {
  signupView: Ref<SignupView>
  socialView: Ref<Raw<SocialAuthComponentView> | null>
  authMode: Ref<AuthMode>
  initSocialAuth: () => Promise<void>
  prefillEmail: (email?: string) => void
  resetSignupView: () => void
  resetSocialAuthValidation: () => void
  stopRegistering: () => void
  startEmailSignup: () => Promise<void>
}

/**
 * Create shared signup auth state for onboarding and modal surfaces.
 */
export function useSignupAuth(options: SignupAuthOptions): SignupAuthState {
  const wait = useWait()

  const socialView = ref<Raw<SocialAuthComponentView> | null>(null)
  const authMode = ref<AuthMode>(AuthMode.Signup)

  /**
   * Mark the shared auth flow as busy.
   */
  function startRegistering(): void {
    wait.start('Register')
  }

  /**
   * Clear the shared auth loading state.
   */
  function stopRegistering(): void {
    wait.end('Register')
  }

  /**
   * Route successful auth results to the caller-specific completion handler.
   */
  async function handleAuthSuccess(
    nextPage: RouteLocationRaw | null,
  ): Promise<void> {
    try {
      await options.onSuccess(nextPage)
    } finally {
      stopRegistering()
    }
  }

  /**
   * Build a fresh signup view bound to the shared auth callbacks.
   */
  function createSignupView(): SignupView {
    return new SignupView(startRegistering, handleAuthSuccess, stopRegistering)
  }

  const signupView = ref(createSignupView())

  /**
   * Create and initialize the shared social auth view on demand.
   */
  async function initSocialAuth(): Promise<void> {
    // Keep social auth behind a separate webpack async boundary so the
    // signup modal can open with email signup first and load providers after.
    const { createSocialAuthView } = await import('./social')
    socialView.value = await createSocialAuthView(
      startRegistering,
      handleAuthSuccess,
      stopRegistering,
    )
    socialView.value?.init()
  }

  /**
   * Pre-fill the email field before the signup flow starts.
   */
  function prefillEmail(email = ''): void {
    signupView.value.model.email = email
  }

  /**
   * Reset the email signup form to its initial state.
   */
  function resetSignupView(): void {
    signupView.value = createSignupView()
  }

  /**
   * Clear social auth validation state without recreating the provider views.
   */
  function resetSocialAuthValidation(): void {
    socialView.value?.validation.reset()
  }

  /**
   * Start the email signup flow with the current form data.
   */
  async function startEmailSignup(): Promise<void> {
    await signupView.value.start()
  }

  return {
    signupView,
    socialView,
    authMode,
    initSocialAuth,
    prefillEmail,
    resetSignupView,
    resetSocialAuthValidation,
    stopRegistering,
    startEmailSignup,
  }
}
